import { AlertCircle, AlertTriangle, ArrowDownRight, ArrowUpRight, Banknote, Check, CirclePlus, Clock3, Coins, Info, RefreshCw, ShieldCheck, Sparkles, TrendingUp, WalletCards, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PortfolioSummaryDto } from '../../shared/types';
import { api } from '../api';
import { AllocationRing, PortfolioChart } from '../components/Charts';
import { IconBox, StatusDot } from '../components/Icons';
import { HoldingsSection } from '../components/HoldingsSection';
import { displayMoney, eventLabel, formatDate, formatNumber, localDateTimeInputValue, type DisplayCurrency } from '../format';
import { buildPriceRefreshProgress, parseRefreshResults, type PriceRefreshProgress } from '../price-refresh';

export function Dashboard({ summary, currency, setCurrency, reload, canLoadDemo, isLocalDemo }: {
  summary: PortfolioSummaryDto;
  currency: DisplayCurrency;
  setCurrency: (currency: DisplayCurrency) => void;
  reload: () => Promise<void>;
  canLoadDemo?: boolean;
  isLocalDemo?: boolean;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<PriceRefreshProgress | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [manualHolding, setManualHolding] = useState<PortfolioSummaryDto['holdings'][number] | null>(null);
  const totalReturn = Number(summary.stats.totalReturnUsd);

  async function checkPrices() {
    setRefreshing(true);
    setNotice(null);
    setRefreshProgress(buildPriceRefreshProgress('QUEUED', []));
    try {
      const job = await api<{ id: string }>('/prices/refresh', { method: 'POST' });
      let visibleUpdates = 0;
      let finished = false;
      for (let attempt = 0; attempt < 300; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        const status = await api<{ status: string; errorMessage?: string; resultJson?: string }>('/prices/refresh/' + job.id);
        const results = parseRefreshResults(status.resultJson);
        const progress = buildPriceRefreshProgress(status.status, results);
        setRefreshProgress(progress);
        if (progress.visibleUpdates > visibleUpdates && !['COMPLETED', 'PARTIAL', 'FAILED'].includes(status.status)) {
          visibleUpdates = progress.visibleUpdates;
          await reload().catch(() => undefined);
        }
        if (['COMPLETED', 'PARTIAL', 'FAILED'].includes(status.status)) {
          finished = true;
          const manualRequired = results.filter((result) => result.status === 'MANUAL_REQUIRED').length;
          const cached = results.filter((result) => result.status === 'CACHED').length;
          const stale = results.filter((result) => ['STALE_FALLBACK', 'STALE_CACHED'].includes(result.status)).length;
          if (status.status === 'COMPLETED') {
            const details = [
              cached ? `ใช้ cache ${cached} รายการ` : '',
              manualRequired ? `หุ้นไทย ${manualRequired} รายการรอกรอกราคาเอง` : '',
            ].filter(Boolean).join(' · ');
            setNotice(details ? `ตรวจสอบราคาเรียบร้อย · ${details}` : 'อัปเดตราคาเรียบร้อย');
          } else if (stale) {
            setNotice(`Provider บางรายการไม่พร้อม · ใช้ราคาเดิม ${stale} รายการ`);
          } else {
            setNotice(status.errorMessage || 'อัปเดตได้บางรายการ');
          }
          await reload();
          break;
        }
      }
      if (!finished) setNotice('งานอัปเดตยังทำต่อเบื้องหลัง กรุณาเปิดหน้านี้ไว้เพื่อรับราคาอัตโนมัติ');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'อัปเดตราคาไม่สำเร็จ');
      setRefreshProgress((current) => current ? {
        ...current, label: 'ติดตามสถานะราคาไม่สำเร็จ', detail: 'ลองกด Check ราคาอีกครั้ง', tone: 'warning',
      } : null);
    } finally {
      setRefreshing(false);
    }
  }

  async function loadDemoPortfolio() {
    setLoadingDemo(true);
    setNotice(null);
    try {
      await api('/admin/demo-portfolio', { method: 'POST' });
      await reload();
      setNotice('ใส่ข้อมูลตัวอย่างเรียบร้อย รายการเดโมมีเลขอ้างอิงขึ้นต้นด้วย DEMO-');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'ใส่ข้อมูลตัวอย่างไม่สำเร็จ');
    } finally {
      setLoadingDemo(false);
    }
  }

  return <>
    <section className="page-heading">
      <div><div className="eyebrow">ภาพรวมพอร์ต</div><h1>สวัสดี วันนี้พอร์ตเป็นอย่างไรบ้าง</h1><p>ข้อมูลทั้งหมดเป็นของคุณและไม่แชร์กับสมาชิกคนอื่น</p></div>
      <div className="heading-actions">
        <div className="currency-toggle" role="group" aria-label="สกุลเงินที่แสดง">
          {(['USD', 'THB'] as const).map((item) => <button key={item} className={currency === item ? 'active' : ''} onClick={() => setCurrency(item)}>{item}</button>)}
        </div>
        <button className="button secondary" onClick={checkPrices} disabled={refreshing}>
          <RefreshCw size={17} className={refreshing ? 'spin' : ''} /> {refreshing ? 'กำลังตรวจ…' : 'Check ราคา'}
        </button>
        <Link to="/upload" className="button primary"><CirclePlus size={17} /> เพิ่มข้อมูล</Link>
      </div>
    </section>
    {isLocalDemo && <div className="notice"><Info size={16} />พอร์ตทดลองบนเครื่องนี้ · ข้อมูลและราคาใช้สำหรับลองหน้าจอ</div>}
    {refreshProgress && <div className={`price-refresh-progress ${refreshProgress.tone}`} role="status" aria-live="polite">
      <div className="price-refresh-progress-head">
        <span><RefreshCw size={15} className={refreshing ? 'spin' : ''} /><strong>{refreshProgress.label}</strong></span>
        <b>{refreshProgress.percent}%</b>
      </div>
      <div className="price-refresh-progress-track" role="progressbar" aria-label="ความคืบหน้าการอัปเดตราคา"
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={refreshProgress.percent}>
        <span style={{ width: `${refreshProgress.percent}%` }} />
      </div>
      <small>{refreshProgress.detail}</small>
    </div>}
    {notice && <div className="notice"><Check size={16} />{notice}</div>}

    <section className="hero-card">
      <div className="hero-copy">
        <span className="hero-label"><WalletCards size={16} /> มูลค่าพอร์ตรวม</span>
        <div className="hero-value">{displayMoney(summary.stats.marketValueUsd, currency, summary.usdThb)}</div>
        <div className={`return-pill ${totalReturn >= 0 ? 'positive' : 'negative'}`}>
          {totalReturn >= 0 ? <ArrowUpRight size={17} /> : <ArrowDownRight size={17} />}
          {totalReturn >= 0 ? '+' : ''}{displayMoney(totalReturn, currency, summary.usdThb)} ผลตอบแทนรวม
        </div>
      </div>
      <div className="hero-meta">
        <span>THB/USD</span><strong>{Number(summary.usdThb).toFixed(2)}</strong>
        <small>อัตราอ้างอิงล่าสุด</small>
      </div>
      <div className="hero-glow hero-glow-one" /><div className="hero-glow hero-glow-two" />
    </section>

    <section className="stat-grid">
      <div className="stat-card"><IconBox icon={Banknote} /><span>เงินลงทุนสุทธิ</span><strong>{displayMoney(summary.stats.netContributionsUsd, currency, summary.usdThb)}</strong><small>กระแสเงินภายนอก</small></div>
      <div className="stat-card"><IconBox icon={TrendingUp} tone="blue" /><span>กำไรที่ยังไม่ขาย</span><strong className={Number(summary.stats.unrealizedPlUsd) >= 0 ? 'gain' : 'loss'}>{displayMoney(summary.stats.unrealizedPlUsd, currency, summary.usdThb)}</strong><small>Unrealized P/L</small></div>
      <div className="stat-card"><IconBox icon={Coins} tone="gold" /><span>กำไรที่ขายแล้ว</span><strong className={Number(summary.stats.realizedPlUsd) >= 0 ? 'gain' : 'loss'}>{displayMoney(summary.stats.realizedPlUsd, currency, summary.usdThb)}</strong><small>Realized P/L</small></div>
      <div className="stat-card"><IconBox icon={Sparkles} tone="green" /><span>ปันผลและดอกเบี้ย</span><strong>{displayMoney(summary.stats.incomeUsd, currency, summary.usdThb)}</strong><small>รายได้สะสม</small></div>
    </section>

    <section className="dashboard-grid">
      <article className="panel wide-panel">
        <div className="panel-head"><div><span className="panel-kicker">PERFORMANCE</span><h2>เส้นทางของพอร์ต</h2></div><span className="as-of"><Clock3 size={14} /> ถึง {formatDate(summary.generatedAt)}</span></div>
        <PortfolioChart history={summary.history} currency={currency} usdThb={summary.usdThb} />
      </article>
      <article className="panel allocation-panel">
        <div className="panel-head"><div><span className="panel-kicker">ALLOCATION</span><h2>สัดส่วนสินทรัพย์</h2></div><Info size={17} className="muted" /></div>
        <AllocationRing data={summary.allocation} currency={currency} usdThb={summary.usdThb} />
      </article>
    </section>

    <HoldingsSection summary={summary} currency={currency} onEditPrice={setManualHolding} emptyAction={
      <div className="inline-empty"><CirclePlus size={30} /><div><strong>เริ่มสร้างพอร์ตของคุณ</strong><p>อัปโหลดสลิป Dime! หรือเพิ่มรายการแรกด้วยตนเอง</p></div><div className="inline-empty-actions">{canLoadDemo && <button className="button secondary small" onClick={loadDemoPortfolio} disabled={loadingDemo}><Sparkles size={15} /> {loadingDemo ? 'กำลังใส่ข้อมูล…' : 'ใส่ข้อมูลตัวอย่าง'}</button>}<Link className="button primary small" to="/upload">เพิ่มข้อมูล</Link></div></div>
    } />

    <section className="dashboard-grid lower-grid">
      <article className="panel">
        <div className="panel-head"><div><span className="panel-kicker">RECENT</span><h2>รายการล่าสุด</h2></div></div>
        <div className="activity-list">
          {summary.recentTransactions.map((item) => <div className="activity-row" key={item.id}>
            <span className={`activity-icon ${item.eventType === 'SELL' ? 'sell' : ''}`}>{item.eventType === 'SELL' ? <ArrowUpRight /> : <ArrowDownRight />}</span>
            <span><strong>{eventLabel[item.eventType] || item.eventType} {item.symbol || ''}</strong><small>{item.accountName} · {formatDate(item.occurredAt)}</small></span>
            <strong>{item.grossAmount ? `${formatNumber(item.grossAmount, 2)} ${item.currency || ''}` : item.quantity ? formatNumber(item.quantity) : '—'}</strong>
          </div>)}
          {!summary.recentTransactions.length && <p className="muted empty-small">ยังไม่มีรายการ</p>}
        </div>
      </article>
      <article className="panel health-panel">
        <div className="panel-head"><div><span className="panel-kicker">DATA HEALTH</span><h2>คุณภาพข้อมูล</h2></div><ShieldCheck size={20} /></div>
        <div className="health-score"><strong>{summary.health.staleQuotes + summary.health.missingQuotes + summary.health.pendingDrafts === 0 ? 'พร้อม' : 'ควรตรวจ'}</strong><span>สถานะข้อมูลพอร์ต</span></div>
        <div className="health-list">
          <div><StatusDot status={summary.health.pendingDrafts ? 'STALE' : 'FRESH'} /><span>สลิปรอตรวจ</span><strong>{summary.health.pendingDrafts}</strong></div>
          <div><StatusDot status={summary.health.staleQuotes ? 'STALE' : 'FRESH'} /><span>ราคาที่เก่า</span><strong>{summary.health.staleQuotes}</strong></div>
          <div><StatusDot status={summary.health.missingQuotes ? 'MISSING' : 'FRESH'} /><span>ยังไม่มีราคา</span><strong>{summary.health.missingQuotes}</strong></div>
        </div>
        {(summary.health.pendingDrafts > 0 || summary.health.staleQuotes > 0) && <Link to="/review" className="button tertiary full"><AlertTriangle size={16} /> ตรวจรายการที่ต้องดูแล</Link>}
      </article>
    </section>
    {manualHolding && <ManualPriceModal holding={manualHolding} close={() => setManualHolding(null)} saved={async () => { setManualHolding(null); await reload(); }} />}
    <p className="disclaimer">ข้อมูลราคาและผลตอบแทนมีไว้เพื่อการติดตามเท่านั้น ไม่ใช่คำแนะนำในการลงทุน</p>
  </>;
}

function ManualPriceModal({ holding, close, saved }: {
  holding: PortfolioSummaryDto['holdings'][number]; close: () => void; saved: () => Promise<void>;
}) {
  const [price, setPrice] = useState(holding.currentPrice || '');
  const [currency, setCurrency] = useState(holding.quoteCurrency || holding.instrument.quoteCurrency);
  const [asOf, setAsOf] = useState(localDateTimeInputValue());
  const [error, setError] = useState<string | null>(null);
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError(null);
    try {
      await api('/prices/manual', { method: 'POST', body: JSON.stringify({ instrumentId: holding.instrumentId, price, currency, asOf: new Date(asOf).toISOString() }) });
      await saved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'บันทึกราคาไม่สำเร็จ'); }
  }
  async function reset() {
    await api(`/prices/manual/${holding.instrumentId}`, { method: 'DELETE' });
    await saved();
  }
  return <div className="modal-backdrop"><form className="modal compact-modal" onSubmit={submit}>
    <div className="modal-head"><div><span className="panel-kicker">MANUAL PRICE</span><h2>ราคาประเมิน {holding.instrument.symbol}</h2></div><button type="button" className="icon-button" onClick={close}><X /></button></div>
    {holding.instrument.kind === 'GOLD' && <div className="validation-box"><AlertCircle /><span><strong>ราคาทองเป็นค่าประเมิน</strong><small>ราคา spot ไม่ใช่ราคาขายคืนที่ Dime! รับประกัน</small></span></div>}
    <div className="form-grid">
      <label className="field"><span>ราคาต่อ {holding.instrument.unit}</span><input required min="0.00000001" step="any" type="number" value={price} onChange={(event) => setPrice(event.target.value)} /></label>
      <label className="field"><span>สกุลเงิน</span><select value={currency} onChange={(event) => setCurrency(event.target.value)}><option>USD</option><option>THB</option></select></label>
      <label className="field field-wide"><span>เวลาอ้างอิง</span><input required type="datetime-local" value={asOf} onChange={(event) => setAsOf(event.target.value)} /></label>
    </div>
    {error && <p className="form-error">{error}</p>}
    <div className="modal-actions">{holding.quoteStatus === 'MANUAL' ? <button type="button" className="button danger-text" onClick={reset}>กลับไปใช้ราคาจาก Provider</button> : <span />}<button className="button primary">บันทึกราคา</button></div>
  </form></div>;
}
